import { useEffect, useMemo, useRef } from 'react'

const W = 320
const VOICES = [
  { name: 'deep voice', color: '#3a9bff', freq: 2.0, y: 30 },
  { name: 'bright voice', color: '#ff5aa0', freq: 3.6, y: 80 },
  { name: 'child voice', color: '#46e8b0', freq: 6.0, y: 130 },
]
const GLYPH_PTS = 40
const GLYPH_W = 110

function gauss() {
  return (Math.random() + Math.random() + Math.random() - 1.5) * 26
}

// The encoder maps audio into a space where POSITION is set by meaning, not
// the raw voice: the same word "hello" spoken in three different voices lands
// in the same cluster, while other words occupy other regions.
export function Scene07Embeddings2D() {
  const pathRefs = useRef([])
  const raf = useRef(0)

  const helloCenter = { x: 160, y: 130 }
  const yesCenter = { x: 250, y: 55 }
  const stopCenter = { x: 65, y: 210 }

  const helloDots = useMemo(
    () => Array.from({ length: 42 }, () => ({
      x: helloCenter.x + gauss(), y: helloCenter.y + gauss() * 0.8,
      color: VOICES[Math.floor(Math.random() * 3)].color,
    })), []
  )
  const yesDots = useMemo(
    () => Array.from({ length: 26 }, () => ({
      x: yesCenter.x + gauss() * 0.7, y: yesCenter.y + gauss() * 0.6,
      color: VOICES[Math.floor(Math.random() * 3)].color,
    })), []
  )
  const stopDots = useMemo(
    () => Array.from({ length: 26 }, () => ({
      x: stopCenter.x + gauss() * 0.7, y: stopCenter.y + gauss() * 0.6,
      color: VOICES[Math.floor(Math.random() * 3)].color,
    })), []
  )

  useEffect(() => {
    let start = performance.now()
    const loop = (now) => {
      const t = (now - start) / 1000
      VOICES.forEach((v, vi) => {
        let d = ''
        for (let i = 0; i <= GLYPH_PTS; i++) {
          const u = i / GLYPH_PTS
          const x = 12 + u * GLYPH_W
          const y = v.y + Math.sin(u * v.freq * Math.PI * 2 - t * 4) * 14
          d += `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)} `
        }
        if (pathRefs.current[vi]) pathRefs.current[vi].setAttribute('d', d)
      })
      raf.current = requestAnimationFrame(loop)
    }
    raf.current = requestAnimationFrame(loop)
    return () => cancelAnimationFrame(raf.current)
  }, [])

  return (
    <svg viewBox="0 0 320 500" style={{ width: '100%', maxWidth: 340, height: 'auto', display: 'block', margin: '0 auto' }}>
      <style>{`
        @keyframes embedLanding { 0%, 100% { r: 5; } 50% { r: 6.5; } }
      `}</style>

      {/* left: same word, three voices */}
      {VOICES.map((v, i) => (
        <g key={v.name}>
          <path ref={(el) => { pathRefs.current[i] = el }} fill="none" stroke={v.color} strokeWidth="2" strokeLinecap="round" />
          <text x={12} y={v.y - 20} fill={v.color} fontSize="10" fontFamily="system-ui, sans-serif">{v.name}</text>
        </g>
      ))}
      <text x="12" y="172" fill="#cfe0ec" fontSize="13" fontFamily="system-ui, sans-serif">"hello"</text>
      <text x="12" y="188" fill="#5d7891" fontSize="9" fontFamily="monospace" letterSpacing="0.06em">one word · three voices</text>

      <text x={W / 2} y="212" fill="#5577aa" fontSize="18" textAnchor="middle">↓</text>

      {/* embedding space */}
      <rect x="10" y="230" width="300" height="230" fill="none" stroke="#2a4a66" strokeWidth="1" opacity="0.5" />
      <g transform="translate(0, 230)">
        {yesDots.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r="2.6" fill={p.color} opacity="0.75" />)}
        <text x={yesCenter.x} y={yesCenter.y - 22} fill="#9fb6c4" fontSize="11" fontFamily="system-ui, sans-serif" textAnchor="middle">yes</text>

        {stopDots.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r="2.6" fill={p.color} opacity="0.75" />)}
        <text x={stopCenter.x} y={stopCenter.y - 22} fill="#9fb6c4" fontSize="11" fontFamily="system-ui, sans-serif" textAnchor="middle">stop</text>

        {helloDots.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r="2.8" fill={p.color} opacity="0.85" />)}
        {VOICES.map((v, i) => (
          <circle key={v.name} cx={helloCenter.x + (i - 1) * 14} cy={helloCenter.y + (i % 2 === 0 ? -8 : 8)} r="5" fill={v.color}
            style={{ transformOrigin: `${helloCenter.x + (i - 1) * 14}px ${helloCenter.y + (i % 2 === 0 ? -8 : 8)}px`, animation: `embedLanding 1.6s ease-in-out infinite`, animationDelay: `${i * 0.2}s` }} />
        ))}
        <text x={helloCenter.x} y={helloCenter.y - 32} fill="#dfeaf2" fontSize="13" fontFamily="system-ui, sans-serif" textAnchor="middle">hello</text>
      </g>

      <text x={W / 2} y="484" fill="#5d7891" fontSize="9" fontFamily="monospace" textAnchor="middle" letterSpacing="0.06em">position = meaning · colour = speaker</text>
    </svg>
  )
}
